import Service, { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import ApiService from 'cio/services/api';
import ObjectTypesService from 'cio/services/object-types';
import DS from 'ember-data';
import { task, timeout } from 'ember-concurrency';
import CustomerModel from 'cio/models/customer';
import ObjectModel from 'cio/models/object';

interface FetchRelationshipsOptions {
  model: CustomerModel | ObjectModel;
  objectTypeId: string;
  itemsPerPage: number;
  page?: number;
  delay?: number;
}

export default class RelationshipsService extends Service {
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service objectTypes!: ObjectTypesService;

  fetchRelationships = task(async (options: FetchRelationshipsOptions) => {
    let { model, objectTypeId, itemsPerPage, page, delay } = options ?? {};

    if (delay != null) {
      await timeout(delay);
    }

    let { relationships, meta } =
      model instanceof CustomerModel
        ? await model.getRelatedObjects(objectTypeId, {
            size: itemsPerPage,
            page: page ?? 1,
          })
        : await model.getRelatedCustomers({
            size: itemsPerPage,
            page: page ?? 1,
          });

    return { relationships, meta };
  });

  fetchRelationshipsForObjectTypes = task(async (options: Pick<FetchRelationshipsOptions, 'model' | 'delay'>) => {
    return await Promise.all(
      this.objectTypes.sortedObjectTypes.map(async ({ id: objectTypeId }) => {
        let { relationships } = await this.fetchRelationships.perform({
          model: options.model,
          objectTypeId,
          itemsPerPage: 1,
          delay: options?.delay,
        });

        return {
          id: objectTypeId,
          relationships,
        };
      }),
    );
  });
}

declare module '@ember/service' {
  interface Registry {
    relationships: RelationshipsService;
  }
}
